
  import { defineComponent } from "vue";
  import { mapStores } from "pinia";

  import { get, has, isEmpty, without } from "lodash";

  import CatalogSelect from "@/components/processComponents/CatalogSelect.vue";
  import ConditionalRenderer from "@/components/processComponents/ConditionalRenderer.vue";
  import ConditionalSelect from "@/components/processComponents/ConditionalSelect.vue";
  import CurrencyInput from "@/components/processComponents/CurrencyInput.vue";
  import DecimalInput from "@/components/processComponents/DecimalInput.vue";
  import DmClientProductDocuments from "@/components/processComponents/DmClientProductDocuments.vue";
  import FileUpload from "@/components/processComponents/FileUpload.vue";
  import FileUploadV2 from "@/components/processComponents/v2/FileUploadV2.vue";
  import FormContainer from "@/components/processComponents/FormContainer.vue";
  import FormGroupV2 from "@/components/processComponents/v2/FormGroupV2.vue";
  import FormTitle from "@/components/processComponents/FormTitle.vue";
  import HiddenInput from "@/components/processComponents/HiddenInput.vue";
  import IntegerInput from "@/components/processComponents/IntegerInput.vue";
  import MessageBox from "@/components/processComponents/MessageBox.vue";
  import NestedFormContainer from "@/components/processComponents/NestedFormContainer.vue";
  import QuoteResultsRender from "@/components/processComponents/QuoteResultsRender.vue";
  import SimpleCheckbox from "@/components/processComponents/SimpleCheckbox.vue";
  import SimpleContainer from "@/components/processComponents/v2/SimpleContainer.vue";
  import SimpleContainerV2 from "@/components/processComponents/v2/SimpleContainerV2.vue";
  import SimpleDate from "@/components/processComponents/SimpleDate.vue";
  import SimpleDateTime from "@/components/processComponents/SimpleDateTime.vue";
  import SimpleEmail from "@/components/processComponents/SimpleEmail.vue";
  import SimpleInput from "@/components/processComponents/SimpleInput.vue";
  import SimpleInputV2 from "@/components/processComponents/v2/SimpleInputV2.vue";
  import SimpleNumber from "@/components/processComponents/SimpleNumber.vue";
  import SimplePhone from "@/components/processComponents/SimplePhone.vue";
  import SimpleLabel from "@/components/processComponents/SimpleLabel.vue";
  import SimpleSelect from "@/components/processComponents/SimpleSelect.vue";
  import SimpleSelectV2 from "@/components/processComponents/v2/SimpleSelectV2.vue";
  import SimpleSpan from "@/components/processComponents/SimpleSpan.vue";
  import SimpleTextarea from "@/components/processComponents/SimpleTextarea.vue";
  import SimpleTime from "@/components/processComponents/SimpleTime.vue";
  import SimpleSwitch from "@/components/processComponents/SimpleSwitch.vue";
  import RelationalSelect from "@/components/processComponents/RelationalSelect.vue";
  import { applicationViewModes } from "@/helpers/utils";
  import { useMasterLayoutStore } from "@/stores/MasterLayoutStore";
  import NumericInput from "@/components/processComponents/v2/NumericInput.vue";
  import NumericInputV2 from "@/components/processComponents/v2/NumericInputV2.vue";
  import SimpleSwitchV2 from "@/components/processComponents/v2/SimpleSwitchV2.vue";
  import FormContainerV2 from "@/components/processComponents/v2/FormContainerV2.vue";
  import QuoteResultsRendererV2 from "@/components/processComponents/v2/QuoteResultsRendererV2.vue";

  export default defineComponent({
    name: "ProcessComponentsIterator",

    components: {
      CatalogSelect,
      ConditionalRenderer,
      ConditionalSelect,
      CurrencyInput,
      DecimalInput,
      DmClientProductDocuments,
      FileUpload,
      FileUploadV2,
      FormContainer,
      FormContainerV2,
      FormGroupV2,
      FormTitle,
      HiddenInput,
      IntegerInput,
      MessageBox,
      NestedFormContainer,
      NumericInput,
      NumericInputV2,
      QuoteResultsRender,
      QuoteResultsRendererV2,
      SimpleCheckbox,
      SimpleContainer,
      SimpleContainerV2,
      SimpleDate,
      SimpleDateTime,
      SimpleEmail,
      SimpleInput,
      SimpleInputV2,
      SimpleLabel,
      SimpleNumber,
      SimplePhone,
      SimpleSelect,
      SimpleSelectV2,
      SimpleSpan,
      SimpleSwitch,
      SimpleSwitchV2,
      SimpleTextarea,
      SimpleTime,
      RelationalSelect,
    },

    props: {
      components: {
        type: Array,
        required: true,
        default: () => [],
      },

      indexPosition: {
        type: Number,
        default: () => -1,
      },

      layout: {
        type: String,
        required: false,
        default: () => "horizontal",
      },

      noClass: {
        type: Boolean,
        required: false,
        default: () => false,
      },

      path: {
        type: String,
        required: false,
        default: () => "form.data",
      },

      parentComponentKey: {
        type: String,
        default: () => "",
      },

      viewMode: {
        type: String,
        required: false,
        default: () => applicationViewModes.visualization,
      },
    },

    computed: {
      ...mapStores(useMasterLayoutStore),

      childComponentsValue: {
        get(): any {
          return this.$attrs.modelValue || {};
        },

        set(value: any) {
          this.$emit("update:modelValue", value || this.value);
        },
      },

      isMobile(): boolean {
        return this.masterLayoutStore.mobile;
      },

      isVertical(): boolean {
        return this.layout === "vertical" || this.isMobile;
      },

      value: {
        get(): any {
          return this.$attrs.modelValue || {};
        },

        set(value: any) {
          this.$emit("update:modelValue", value || this.childComponentsValue);
        },
      },
    },

    methods: {
      childComponentsValueListener(event: any) {
        this.childComponentsValue = event;
      },

      valueListener(event: any, processComponentName: string) {
        this.value[processComponentName] = event;
        this.$emit("update:modelValue", { ...this.childComponentsValue, ...this.value });
      },


      buildPath(component: any): any {
        let path = [this.path];

        const attributes = this.getAttributes(component);

        if (has(attributes, "name") && !isEmpty(attributes.name)) {
          if (this.indexPosition >= 0) {
            path.push(this.indexPosition.toString());
          }
          path.push(attributes.name);
        }

        if (has(attributes, "cname")) {
          path.push(attributes.cname);
        }

        return without(path, "").join(".");
      },

      getAttributes(component: any): any {
        return get(
          component,
          "settings.attributes",
          get(component, "attributes", {}),
        );
      },
    },
  });
